import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import { FormattedNumber } from 'react-intl';

import { Button, Drawer, Typography } from 'antd';

import FileSelector from 'components/FileSelector';
import type { FileSelectorHandle } from 'components/FileSelector';
import MaskedOutput from 'components/MaskedOutput';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ActionCollapse from 'v2/components/Shared/QuickView/ActionCollapse';
import { useFiscalDocumentsQuickView } from 'v2/hooks/useFiscalDocumentsQuickView';

import { Icon } from '@eduzz/ui-antd-theme';

import Customer from './Customer';
import Files from './Files';
import Items from './Items';
import { useFiscalDocuments } from '../../context';

const EDUZZ_EMITTER_IDS = [70042526, 52584, 52586, 63545352];

const QuickView: React.FC = () => {
  const fileSelector = useRef<FileSelectorHandle>(null);

  const { selectedData, setSelectedData } = useFiscalDocuments();
  const active = useMemo(() => Boolean(selectedData), [selectedData]);

  const { upload, uploadIsPending, download, downloadIsPending } = useFiscalDocumentsQuickView();

  const handleClose = useCallback(() => {
    setSelectedData(undefined);
  }, [setSelectedData]);

  const handleSelectFile = useCallback(async () => {
    const file = await fileSelector.current.open();

    if (!file) {
      return;
    }

    upload(file);
  }, [upload]);

  const handleClickDownload = useCallback(async () => {
    download(selectedData?.id);
  }, [download, selectedData?.id]);

  return (
    <Fragment>
      <FileSelector ref={fileSelector} />

      <Drawer
        onClose={handleClose}
        placement='right'
        open={active}
        closable
        width='min(450px, 100vw)'
        title={`COD. ${selectedData?.id}`}
      >
        {active && (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <Typography.Text type='secondary'>
                {selectedData.status} -&nbsp;
                <MaskedOutput mask='date' value={selectedData.reference_date} />
              </Typography.Text>
              <Typography.Text strong>{selectedData.display}</Typography.Text>
              <Typography.Text strong type='success'>
                <FormattedNumber value={selectedData.value || 0} format='BRL' />
              </Typography.Text>
            </div>

            <ActionCollapse title='Origem' icon={<Icon.Building2 size={16} />}>
              <Customer data={selectedData.customer_origin} />
            </ActionCollapse>

            <ActionCollapse title='Destino' icon={<Icon.MapPin size={16} />}>
              <Customer data={selectedData.destination_customer} />
            </ActionCollapse>

            <ActionCollapse title='Detalhes' icon={<Icon.FileText size={16} />}>
              <Items />
            </ActionCollapse>

            <ActionCollapse title='Arquivos' icon={<Icon.Paperclip size={16} />}>
              <Files />
            </ActionCollapse>

            {!EDUZZ_EMITTER_IDS.includes(Number(selectedData.customer_origin.id)) && (
              <PermissionHide action='myeduzz:cdf:editar'>
                <div className='text-right'>
                  <Button
                    color='default'
                    variant='filled'
                    disabled={uploadIsPending}
                    onClick={handleSelectFile}
                    loading={uploadIsPending}
                  >
                    {uploadIsPending ? 'Enviando...' : 'Upload de Arquivo'}
                  </Button>
                </div>
              </PermissionHide>
            )}

            {EDUZZ_EMITTER_IDS.includes(Number(selectedData.customer_origin.id)) && (
              <div className='text-right'>
                <Button
                  color='default'
                  variant='filled'
                  disabled={downloadIsPending}
                  onClick={handleClickDownload}
                  loading={downloadIsPending}
                  icon={<Icon.Download size={16} />}
                >
                  {downloadIsPending ? 'Baixando...' : 'Download'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </Fragment>
  );
};

export default QuickView;
